<template>
  <GlossaryContainer>
    <template #header>
      <div :class="$style.header">
        <h1 :class="$style.title">{{ t('glossary.title') }}</h1>

        <div :class="$style.nav">
          <a v-for="group in groups" :key="group.letter" :href="`#${group.letter}`">{{ group.letter }}</a>
        </div>
      </div>
    </template>

    <template #list>
      <div v-for="group in groups" :key="group.letter" :class="$style.group">
        <h2 :id="group.letter" :class="$style.letter">{{ group.letter }}</h2>

        <div v-for="item in group.items" :key="item.id" :class="$style.item">
          <nuxt-link :to="localePath(`/glossary/${item.id}`)">
            {{ item.title }}
          </nuxt-link>
        </div>
      </div>
    </template>

  </GlossaryContainer>
</template>

<script setup lang="ts">
import { eventTracker, AnalyticsEventKey } from '~/utils/analyticServices';
import { wpFetch } from '~/utils/wordpress';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

const localePath = useLocalePath();
const { locale } = useI18n();
const { t } = useI18n();
useHead({
  title: t('meta.blog.title'),
});

const schema: yup.AnyObject = yup.object();

const search = ref<string>('');
const { handleSubmit } = useForm({
  validationSchema: schema,
});

const groups = computed(() => {
  const map = new Map();
  const normalize = (s) => (s || '').trim();
  for (const it of listOfBlog.value) {
    const title = normalize(it.title);
    const first = title.charAt(0).toUpperCase();
    const key = first.match(/[A-ZА-ЯЁЇІЄҐ]/i) ? first : '#';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  // Отсортируем ключи: A..Z..А..Я..#
  const keys = Array.from(map.keys()).sort((a, b) => {
    if (a === '#') return 1;
    if (b === '#') return -1;
    return a.localeCompare(b);
  });
  return keys.map(k => ({ letter: k, items: map.get(k) }));
});

const getPosts = async (): Promise<Record<any, any>[]> => {
  try {
    const response = await wpFetch(`https://wp.0detect.com/wp-json/custom/v1/glossary?meta_key=lang&meta_value=${locale.value}&per_page=9999`);
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error(error);
    return [];
  }
};

const { data: listOfBlog, refresh, status: listStatus } = useAsyncData(
  `glossary-list-${locale.value}`,
  () => getPosts(),
  {
    server: true,
    lazy: false,
    default: () => [],
  },
);

onMounted(() => {
  eventTracker(AnalyticsEventKey.BlogPageViewed, {
     platform: 'WebSite',
   });
});
</script>

<style lang="scss" module>
  .group {
    display: flex;
    flex-direction: column;
  }

  .letter {
    margin: 0 0 12px 0;
    color: #121E29;
    font-size: 32px;
    font-weight: 600;
    line-height: normal;
  }

  .item {
    margin-bottom: 16px;

    a {
      color: #121E29;
      font-size: 18px;
      font-weight: 400;
      line-height: normal;
    }
  }

  .nav {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    margin: 32px 0;

    a {
      color: #293EFF;
      font-size: 32px;
      font-weight: 500;
      line-height: normal;
    }
  }

  .title {
    margin: 0;
    color: #121E29;
    font-family: 'Gilroy';
    font-size: 52px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    @media (max-width: 1023px) {
      font-size: 40px;
    }
  }

  .header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 50px 0 32px 0;

    &Wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 26px;

      @media (max-width: 1023px) {
        flex-direction: column-reverse;
        justify-content: flex-start;
        align-items: flex-start;
      }
    }

    &Caption {
      max-width: 340px;
      text-align: right;
      font-size: 18px;

      @media (max-width: 1023px) {
        width: 100%;
        text-align: left;
        font-size: 14px;
      }
    }

    &Search {
      display: flex;
      align-items: center;
      width: 100%;
      max-width: 520px;

      label {
        flex: 2;
        margin-bottom: 0 !important;
      }

      button {
        flex: 1;
      }

      @media (max-width: 767px) {
        flex-direction: column;
        gap: 22px;

        > * {
          flex: unset;
          width: 100%;
        }
      }
    }
  }
</style>